import { z } from "zod";

export const characterKindSchema = z.enum(["named", "group", "creature", "vehicle"]);

/** Kebab-case id for a significant look, such as `baby` or `cat`. */
export const appearanceIdSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const appearanceSchema = z.object({
  id: appearanceIdSchema,
  label: z.string().min(1),
  /** Public URL set at compile time when this look has its own portrait. */
  imageUrl: z.string().min(1).optional(),
});

export const characterSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  aliases: z.array(z.string()).default([]),
  kind: characterKindSchema.default("named"),
  firstSceneId: z.string().optional(),
  /**
   * Looks that differ from this character's usual self. The draft names them.
   * The usual look is the absence of an appearance, not an entry here.
   */
  appearances: z.array(appearanceSchema).default([]),
  /** Public URL set at compile time when a default portrait file exists. */
  imageUrl: z.string().min(1).optional(),
  /** Set when chapters are joined: the name changes from that chapter on. */
  laterNames: z.array(z.object({
    chapterId: z.string().min(1),
    displayName: z.string().min(1),
    /** The scene it changes, when that is partway through the chapter. */
    sceneId: z.string().min(1).optional(),
  })).optional(),
  /** On a chapter's cast: the scene in this chapter where this name is first learned. */
  namedInScene: z.string().min(1).optional(),
  /** What they are called before that scene, for a chapter played on its own. */
  earlierName: z.string().min(1).optional(),
  /** Set at compile time on a younger self who shares the stage with this character. */
  flashbackOf: z.string().min(1).optional(),
  /** Set at compile time from characters/minor.json. */
  minor: z.boolean().optional(),
  /**
   * Disc color when a color is part of who they are. The draft sets `#rrggbb`.
   * Minor characters without one share a neutral disc.
   */
  markerColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});

export const charactersFileSchema = z.object({
  characters: z.array(characterSchema).min(1),
});

export type Appearance = z.infer<typeof appearanceSchema>;
export type Character = z.infer<typeof characterSchema>;
export type CharactersFile = z.infer<typeof charactersFileSchema>;
